/**
 * Payment Checkout Component
 * Handles Stripe checkout flow
 */

import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {CreditCard, Loader, AlertCircle, CheckCircle} from 'lucide-react';
import {createCheckoutSession, PlanType} from '../../services/SubscriptionService';

interface PaymentCheckoutProps {
  organizationId: string;
  planType: PlanType;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const PaymentCheckout: React.FC<PaymentCheckoutProps> = ({
  organizationId,
  planType,
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Auto-start checkout when component mounts
    handleCheckout();
  }, []);

  const handleCheckout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const {url} = await createCheckoutSession(
        organizationId,
        planType,
        undefined, // quantity - optional, defaults to 1
        `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
        `${window.location.origin}/subscription/cancel`,
      );

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white dark:bg-slate-800 rounded-xl shadow-lg">
        <div className="flex items-center justify-center mb-4">
          <AlertCircle className="w-12 h-12 text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">
          Payment Error
        </h3>
        <p className="text-center text-gray-600 dark:text-gray-300 mb-6">{error}</p>
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/subscription')}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500"
          >
            Go Back
          </button>
          <button
            onClick={handleCheckout}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Redirecting to Payment
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          You'll be redirected to our secure payment page in a moment...
        </p>

        <div className="flex justify-center">
          <Loader className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
        </div>

        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          If you're not redirected automatically,{' '}
          <button
            onClick={handleCheckout}
            className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            click here
          </button>
        </p>
      </div>
    </div>
  );
};

export default PaymentCheckout;

